import { api } from "./client";
import type { BuyerLeadSource, BuyerType } from "./buyers";

/**
 * Buyer Smart Import — the client mirror of
 * `Core/DTOs/Buyers/BuyerSmartImportDtos.cs`.
 *
 * <p>The flow, and why it is three requests rather than one: `analyze` reads
 * the file and proposes a mapping and NOTHING is created; `preview` is a
 * server dry-run re-run on every change to the review state; `commit` queues a
 * background job. The uploaded file is NEVER persisted — it rides every one of
 * the three as a multipart part, and the two hashes are what the client saw at
 * preview time so the server can refuse a file or a plan that moved (D5).</p>
 *
 * <p>🔴 <b>Deliberately its own module rather than a section of
 * `api/buyers.ts`.</b> Smart Import's request bodies are multipart with a JSON
 * side-car, its reads are polled, and its own vocabulary (targets, transforms,
 * sentinels) has no relationship to the buyer record's shape. Folding it into
 * the 3,000-line buyer client would put four unrelated concerns behind one
 * import.</p>
 */

// ---------------------------------------------------------------------------
// The mapping plan
// ---------------------------------------------------------------------------

/** How ONE source column becomes a stored value. */
export interface BuyerImportColumnPlanDto {
  /** 0-based position in the tab's header row. */
  index: number;
  /** The header text as it appears in the file. */
  header: string;
  /** A member of `BUYER_IMPORT_TARGETS`. */
  target: string;
  /** A member of `BUYER_IMPORT_TRANSFORMS`. */
  transform: string;
  /** Only `dateText` reads this — the .NET custom format string. */
  format?: string | null;
  /** Values in THIS column that mean "not provided", beyond the built-ins. */
  sentinels: string[];
  /** 0-100. Highlighted when low; it changes no behaviour on its own. */
  confidence: number;
  /** One sentence, shown in the review table. */
  reason?: string | null;
}

/** What the file itself suggests about where these leads came from. */
export interface BuyerImportSourceSuggestionDto {
  source?: string | null;
  sourceDetail?: string | null;
}

/** The plan for one tab (a CSV or a paste has exactly one). */
export interface BuyerImportTabPlanDto {
  tabName: string;
  /** False for a tab holding no leads — a summary sheet, a legend. Lead tabs
   *  are ticked by default in the review screen; this is not. */
  isLeadTab: boolean;
  /** 1-based RAW row index of the header. Not always 1. */
  headerRowIndex: number;
  sourceSuggestion?: BuyerImportSourceSuggestionDto | null;
  columns: BuyerImportColumnPlanDto[];
}

export interface BuyerImportPlanDto {
  tabs: BuyerImportTabPlanDto[];
}

/**
 * How a plan was arrived at, which is what the review screen labels it.
 * Mirrors `BuyerImportPlanOrigins`.
 */
export type BuyerImportPlanOrigin =
  /** Every header was already in the vocabulary, so no model was asked. */
  | "exact-match"
  /** A previous batch mapped a tab with this same header signature. */
  | "reused"
  /** Proposed by Claude. */
  | "ai"
  /** The AI call failed and the operator is mapping by hand (D19). */
  | "manual-fallback";

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

/** One tab as the reader found it, independent of any mapping. */
export interface BuyerImportTabSummaryDto {
  tabName: string;
  dataRowCount: number;
  columnCount: number;
  headers: string[];
  /**
   * The first rows of the tab, RAW — index 0 is raw row 1, capped at 20, long
   * cells cut.
   *
   * <p>🔴 <b>The header row is still in here, and that is deliberate.</b>
   * `headerRowIndex` is part of the REVIEW STATE and the operator may move it,
   * so anything the server sliced would go stale the moment they did — and
   * refreshing it would cost another upload of the whole file, because nothing
   * about the file is stored between requests (D5).</p>
   *
   * <p>One array serves both readers. The header-row selector offers these
   * rows unsliced, which is why the cap is 20 rather than 3 — D3 tries each of
   * the first 20 as a header candidate. D4's three sample values are the same
   * array from index `headerRowIndex` on, which is where the DATA starts.</p>
   */
  sampleRows: string[][];
}

export interface BuyerSmartImportAnalysisDto {
  fileName: string;
  /** SHA-256 of the uploaded bytes. The commit recomputes it (D5). */
  contentHash: string;
  tabs: BuyerImportTabSummaryDto[];
  plan: BuyerImportPlanDto;
  planOrigin: BuyerImportPlanOrigin;
  /** Set when `planOrigin` is `reused` — the date of the batch it came from. */
  reusedFromBatchAt?: string | null;
  /** Present when the model failed. The plan is still usable (D19). */
  aiFailureMessage?: string | null;
  /** Columns dropped to Notes because their target or transform was outside
   *  the closed vocabulary (X3), and anything else worth seeing. */
  warnings: string[];
  /** True when a listing-title column was identified, so the client knows to
   *  ask for interests in its own request (X5). */
  interestsAvailable: boolean;
}

// ---------------------------------------------------------------------------
// Interests (D8)
// ---------------------------------------------------------------------------

export interface BuyerImportListingInterestDto {
  /** The listing number when one is mapped, else the title. */
  key: string;
  title: string;
  industries: string[];
  /** Only places actually named in the title; a listing naming none
   *  contributes none. */
  geographies: string[];
}

export interface BuyerSmartImportInterestsDto {
  listings: BuyerImportListingInterestDto[];
  aiFailureMessage?: string | null;
}

// ---------------------------------------------------------------------------
// Review state
// ---------------------------------------------------------------------------

export interface BuyerSmartImportTabSelectionDto {
  tabName: string;
  include: boolean;
  headerRowIndex: number;
  /** The tab's Source, pre-set from the AI suggestion and editable. A row's
   *  own mapped Source column still wins over it (D16). */
  source: BuyerLeadSource;
  /**
   * The free text behind the Source — "BizBuySell", the event's name, the list
   * vendor — pre-set from the same suggestion and editable.
   *
   * <p>🔴 <b>It rides the REVIEW STATE because the engine never sees the
   * plan.</b> The suggestion is a `{source, sourceDetail}` pair on
   * `BuyerImportPlanDto`, and the compose step receives only the raw file and
   * this review state — so a detail left on the plan is unreachable, and every
   * buyer out of a listing-site file lands `Source: ListingSite` with no record
   * of WHICH site. The enum half being right is what makes that failure
   * silent.</p>
   */
  sourceDetail?: string | null;
  columns: BuyerImportColumnPlanDto[];
}

export interface BuyerSmartImportReviewStateDto {
  tabs: BuyerSmartImportTabSelectionDto[];
  /** `YYYY-MM-DD`. "Only import rows received on or after this date." Offered
   *  only when a tab maps `inquiry.receivedAt`; null means all rows (D12). */
  cutoff?: string | null;
  inferInterests: boolean;
  /** The interests table as the operator left it. Empty when
   *  `inferInterests` is false. */
  interests: BuyerImportListingInterestDto[];
  buyerTeamId?: string | null;
  assignToSpecialistUserId?: string | null;
  advertisementId?: string | null;
}

// ---------------------------------------------------------------------------
// Preview / result
// ---------------------------------------------------------------------------

/**
 * 🔴 `rowsRead === created + merged + alreadyImported + beforeCutoff +
 * rejected`, in every preview and every result.
 */
export interface BuyerSmartImportCountsDto {
  rowsRead: number;
  created: number;
  merged: number;
  alreadyImported: number;
  beforeCutoff: number;
  rejected: number;
}

/** Which chip a previewed buyer belongs under. */
export type BuyerSmartImportPreviewChip = "all" | "merged" | "flagged" | "rejected";

/**
 * One buyer exactly as it will be created — the full composed Notes included,
 * because a preview that elides Notes is not a preview of what gets written.
 */
export interface BuyerSmartImportPreviewBuyerDto {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  source: string;
  sourceDetail?: string | null;
  statedPriceBand?: string | null;
  liquidCapital?: number | null;
  netWorth?: number | null;
  maxPurchasePrice?: number | null;
  downPaymentAvailable?: number | null;
  targetIndustries?: string | null;
  targetGeographies?: string | null;
  timelineToPurchase?: string | null;
  industryExperience?: string | null;
  /**
   * The `BuyerType` member name, or null when no buyer-type column set one —
   * the buyer is then an individual. Never inferred from a company column.
   */
  buyerType?: BuyerType | null;
  acquiringCompanyName?: string | null;
  acquiringCompanyTitle?: string | null;
  acquiringCompanyWebsite?: string | null;
  notes?: string | null;
  /**
   * How many FILE ROWS folded into this buyer. 1 means it came from a single
   * row; above 1 means rows were merged, so this is what the Merged chip reads.
   *
   * <p>🔴 <b>NOT {@link inquiryCount}, and the two diverge on ordinary rows.</b>
   * A phone row carrying no message still folds in and adds nothing to the
   * inquiry block, so a buyer merged from two rows where only one carried an
   * inquiry has `sourceRowCount` 2 and `inquiryCount` 1 — which is exactly what
   * makes the inquiry count the wrong test for merged-ness.</p>
   */
  sourceRowCount: number;
  /** How many inquiries are written into this buyer's Notes block (D7). Never
   *  decides merged-ness. */
  inquiryCount: number;
  /** Set when this buyer duplicates one that already exists, or one earlier in
   *  the same file. */
  duplicateOf?: string | null;
  /** True when the match is against a buyer already in the database rather
   *  than one earlier in this file (D6). */
  duplicateIsExisting: boolean;
}

/** A row that will not produce a buyer, with its raw cells. */
export interface BuyerSmartImportRejectedRowDto {
  tabName: string;
  rowNumber: number;
  reason: string;
  cells: string[];
}

export interface BuyerSmartImportPreviewDto {
  contentHash: string;
  /** SHA-256 over the whole approved review state. The commit sends back what
   *  it previewed and is refused if either hash moved. */
  planHash: string;
  counts: BuyerSmartImportCountsDto;
  /** Capped at 10 per chip (D4). */
  buyers: BuyerSmartImportPreviewBuyerDto[];
  rejectedRows: BuyerSmartImportRejectedRowDto[];
  /** Rows whose duplicate is a buyer that already exists. */
  duplicatesOfExistingBuyers: number;
  /** Rows whose duplicate is another buyer in this same file. */
  duplicatesWithinFile: number;
  /** Counted only for rows that actually import. */
  valuesCleared: number;
  /** Cells that matched a sentinel — "not provided", never "cleared" (D15). */
  sentinelsFound: number;
  /** Rows kept despite an unreadable received date (D12). */
  datesUnreadable: number;
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Batches (D13)
// ---------------------------------------------------------------------------

/** String-stored on the wire, like every enum in this module. */
export type BuyerImportBatchStatus =
  | "Queued"
  | "Running"
  | "Completed"
  | "Failed"
  | "Undone";

export interface BuyerSmartImportBatchDto {
  id: string;
  fileName: string;
  status: BuyerImportBatchStatus;
  runByUserId: string;
  runByDisplayName?: string | null;
  createdAt: string;
  startedAtUtc?: string | null;
  finishedAtUtc?: string | null;
  failureReason?: string | null;
  counts: BuyerSmartImportCountsDto;
  undoneAtUtc?: string | null;
  undoneByUserId?: string | null;
  undoArchivedCount?: number | null;
  undoKeptCount?: number | null;
  /** True when THIS caller may undo this batch — the importer, or a
   *  `Buyers.ManageAllTeams` holder (D13). */
  canUndo: boolean;
}

/** A buyer an undo deliberately did not archive, and why. */
export interface BuyerSmartImportUndoKeptDto {
  buyerLeadId: string;
  name: string;
  reason: string;
}

export interface BuyerSmartImportUndoResultDto {
  archivedCount: number;
  kept: BuyerSmartImportUndoKeptDto[];
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** The JSON side-car that rides `preview` and `commit`. */
export interface BuyerSmartImportCommitRequest {
  review: BuyerSmartImportReviewStateDto;
  /** Required on commit, ignored on preview. */
  contentHash?: string | null;
  /** Required on commit, ignored on preview. */
  planHash?: string | null;
}

/**
 * The file, or the pasted cells — one or the other, never both.
 *
 * <p>Every request in this module re-sends it, because nothing about the
 * uploaded file is stored between them (D5).</p>
 */
export interface BuyerSmartImportSource {
  file?: File | null;
  /** Tab-separated cells, as pasted out of a spreadsheet. */
  pastedText?: string | null;
}

/** The multipart field the JSON side-car rides in. */
export const SMART_IMPORT_REQUEST_FIELD = "request";

/**
 * The optional `analyze` field that forces the model to re-map a file the
 * deterministic path would otherwise have handled.
 *
 * <p>🔴 <b>Absent means false, and it is absent on every ordinary analyze.</b>
 * Exact-name matching and plan reuse are the two paths that cost nothing, so
 * they stay the default; this is what "Map with AI instead" sends, and without
 * it that control re-runs the same deterministic answer and visibly does
 * nothing.</p>
 */
export const SMART_IMPORT_FORCE_AI_FIELD = "forceAi";

function sourceForm(source: BuyerSmartImportSource): FormData {
  const form = new FormData();
  if (source.file) form.append("file", source.file);
  else if (source.pastedText) form.append("pastedText", source.pastedText);
  return form;
}

export const buyerSmartImportApi = {
  /**
   * Read the file, propose a mapping. Creates nothing and stores nothing.
   *
   * <p>`forceAi` is what the review screen's "Map with AI instead" sends: it
   * skips the exact-match and plan-reuse paths so the model maps a file those
   * would have handled for free.</p>
   */
  analyze: (source: BuyerSmartImportSource, forceAi = false) => {
    const form = sourceForm(source);
    // Sent ONLY when true — the server reads an absent field as false, so a
    // literal "false" would be a value that means the same as saying nothing.
    if (forceAi) form.append(SMART_IMPORT_FORCE_AI_FIELD, "true");
    return api.upload<BuyerSmartImportAnalysisDto>(
      "/buyers/smart-import/analyze",
      form,
    );
  },

  /**
   * What each listing in the file implies about a buyer who enquired about it
   * (D8). Its own request, so the mapping call and this one are each one
   * Claude call inside one HTTP request (X5).
   *
   * <p>🔴 <b>Not in the workstream contract's pinned table.</b> The analysis
   * DTO's own `interestsAvailable` says the client asks "in its own request",
   * and the two AI calls are budgeted separately, so the route has to exist —
   * but the table does not name it. Reported rather than assumed silently.</p>
   */
  interests: (source: BuyerSmartImportSource, review: BuyerSmartImportReviewStateDto) => {
    const form = sourceForm(source);
    form.append(SMART_IMPORT_REQUEST_FIELD, JSON.stringify({ review }));
    return api.upload<BuyerSmartImportInterestsDto>(
      "/buyers/smart-import/interests",
      form,
    );
  },

  /** The server dry-run behind the review screen. Re-run on every change. */
  preview: (source: BuyerSmartImportSource, review: BuyerSmartImportReviewStateDto) => {
    const form = sourceForm(source);
    form.append(SMART_IMPORT_REQUEST_FIELD, JSON.stringify({ review }));
    return api.upload<BuyerSmartImportPreviewDto>("/buyers/smart-import/preview", form);
  },

  /**
   * Queue the import. Sends back the two hashes the last preview reported; the
   * server recomputes both and refuses on any difference (D5).
   */
  commit: (
    source: BuyerSmartImportSource,
    review: BuyerSmartImportReviewStateDto,
    contentHash: string,
    planHash: string,
  ) => {
    const form = sourceForm(source);
    const body: BuyerSmartImportCommitRequest = { review, contentHash, planHash };
    form.append(SMART_IMPORT_REQUEST_FIELD, JSON.stringify(body));
    return api.upload<BuyerSmartImportBatchDto>("/buyers/smart-import/commit", form);
  },

  batches: () =>
    api.get<BuyerSmartImportBatchDto[]>("/buyers/smart-import/batches"),

  batch: (id: string) =>
    api.get<BuyerSmartImportBatchDto>(`/buyers/smart-import/batches/${id}`),

  undo: (id: string) =>
    api.post<BuyerSmartImportUndoResultDto>(
      `/buyers/smart-import/batches/${id}/undo`,
    ),
};
